import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChartBar, Users, FilmStrip, Flag, Plug, CheckCircle, Prohibit, TrashSimple, ArrowsClockwise, Gift, SlidersHorizontal, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import AdminGifts from "../components/admin/AdminGifts";
import AdminSettings from "../components/admin/AdminSettings";
import AdminOwnerEarnings from "../components/admin/AdminOwnerEarnings";

const TABS = [
  { key: "overview",     label: "Overview",     icon: ChartBar },
  { key: "users",        label: "Users",        icon: Users },
  { key: "posts",        label: "Posts",        icon: FilmStrip },
  { key: "reports",      label: "Reports",      icon: Flag },
  { key: "earnings",     label: "Earnings",     icon: Crown },
  { key: "gifts",        label: "Gifts",        icon: Gift },
  { key: "revenue",      label: "Revenue",      icon: SlidersHorizontal },
  { key: "integrations", label: "Integrations", icon: Plug },
];

function StatusPill({ status }) {
  const map = {
    ok: { c: "bg-jam-green/20 text-jam-green border-jam-green/40", l: "OK" },
    fail: { c: "bg-red-500/20 text-red-300 border-red-500/40", l: "FAIL" },
    off: { c: "bg-white/10 text-cream/50 border-white/15", l: "OFF" },
  };
  const m = map[status] || map.off;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-black tracking-[0.2em] uppercase ${m.c}`}>
      {m.l}
    </span>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [authed, setAuthed] = useState(null); // null=checking, true=ok, false=denied
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [reports, setReports] = useState([]);
  const [integrations, setIntegrations] = useState(null);
  const [q, setQ] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await api.get("/admin/me");
        setAuthed(true);
      } catch {
        setAuthed(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!authed) return;
    if (tab === "overview") loadOverview();
    if (tab === "users") loadUsers();
    if (tab === "posts") loadPosts();
    if (tab === "reports") loadReports();
    if (tab === "integrations") loadIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, authed]);

  const loadOverview = async () => { setLoading(true); try { const { data } = await api.get("/admin/stats"); setStats(data); } finally { setLoading(false); } };
  const loadUsers = async () => { setLoading(true); try { const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}&filter=${userFilter}`); setUsers(data); } finally { setLoading(false); } };
  const loadPosts = async () => { setLoading(true); try { const { data } = await api.get("/admin/posts?limit=60"); setPosts(data); } finally { setLoading(false); } };
  const loadReports = async () => { setLoading(true); try { const { data } = await api.get("/admin/reports?status=open"); setReports(data); } finally { setLoading(false); } };
  const loadIntegrations = async () => { setLoading(true); try { const { data } = await api.get("/admin/integrations"); setIntegrations(data); } finally { setLoading(false); } };

  const toggleBan = async (uid) => {
    try {
      const { data } = await api.post(`/admin/users/${uid}/ban`);
      toast.success(data.is_banned ? "User banned" : "User unbanned");
      loadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleVerify = async (uid) => {
    try {
      const { data } = await api.post(`/admin/users/${uid}/verify`);
      toast.success(data.is_verified ? "Verified ✓" : "Verification revoked");
      loadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deletePost = async (pid) => {
    if (!window.confirm("Permanently delete this post?")) return;
    try { await api.delete(`/admin/posts/${pid}`); toast.success("Post deleted"); loadPosts(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resolveReport = async (rid, action) => {
    try { await api.post(`/admin/reports/${rid}/resolve?action=${action}`); toast.success(`Resolved: ${action}`); loadReports(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (authed === null) {
    return <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/40 text-xs tracking-[0.3em] uppercase animate-pulse">Checking…</div>;
  }
  if (authed === false) {
    return (
      <div className="min-h-[100dvh] bg-ink flex flex-col items-center justify-center px-6 text-center" data-testid="admin-denied">
        <Prohibit size={48} weight="duotone" className="text-red-400 mb-4" />
        <p className="text-cream font-black text-2xl">Admins only.</p>
        <p className="text-cream/50 text-sm mt-2">This dashboard is restricted to staff accounts.</p>
        <button onClick={() => nav("/")} className="mt-6 px-6 py-3 rounded-full bg-papaya text-ink font-black text-xs tracking-[0.2em] uppercase">Back to feed</button>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="admin-dashboard">
      {/* Header */}
      <div className="px-5 pt-6 flex items-center gap-3">
        <Link to="/profile" className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream"><ArrowLeft size={18} /></Link>
        <div>
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Staff</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">Admin Dashboard</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-5 mt-6 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 min-w-max">
          {TABS.map(({ key, label, icon: I }) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-black tracking-[0.2em] uppercase transition-colors ${tab === key ? "bg-papaya text-ink" : "bg-white/5 text-cream/60 hover:text-cream"}`}
            >
              <I size={14} weight="bold" /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-5 mt-6">
        {loading && <p className="text-cream/40 text-xs tracking-[0.3em] uppercase animate-pulse">Loading…</p>}

        {tab === "overview" && stats && (
          <div className="grid grid-cols-2 gap-3" data-testid="admin-overview">
            <Stat label="Users"      value={stats.users_total} sub={`${stats.users_banned} banned`} />
            <Stat label="Verified"   value={stats.users_verified} sub="blue ticks active" />
            <Stat label="Posts"      value={stats.posts_total} />
            <Stat label="Live now"   value={stats.live_active} />
            <Stat label="Reports"    value={stats.reports_open} sub="awaiting review" warn={stats.reports_open > 0} />
            <Stat label="Gifts sent" value={stats.gifts_total} />
            <Stat label="Coin sales" value={`$${stats.revenue_usd_coins.toFixed(2)}`} />
            <Stat label="Verif sales" value={`$${stats.revenue_usd_verification.toFixed(2)}`} />
          </div>
        )}

        {tab === "users" && (
          <div data-testid="admin-users">
            <div className="flex gap-2 mb-3">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadUsers()}
                placeholder="Search username or email"
                data-testid="admin-user-search"
                className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-papaya"
              />
              <select value={userFilter} onChange={(e) => { setUserFilter(e.target.value); setTimeout(loadUsers, 50); }} className="bg-white/5 border border-white/10 rounded-2xl px-3 text-cream text-sm">
                <option value="all">All</option>
                <option value="banned">Banned</option>
                <option value="verified">Verified</option>
              </select>
              <button onClick={loadUsers} className="px-3 py-2.5 rounded-2xl bg-papaya text-ink text-xs font-black tracking-[0.2em] uppercase">Go</button>
            </div>
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id} data-testid={`admin-user-${u.id}`} className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-papaya/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {u.avatar_path ? <img src={fileUrl(u.avatar_path)} alt="" className="w-full h-full object-cover" /> : <span className="text-cream font-black">{(u.username || "?")[0].toUpperCase()}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-cream font-bold text-sm truncate flex items-center gap-1">
                      @{u.username}
                      {u.is_verified && <CheckCircle size={12} weight="fill" className="text-papaya" />}
                      {u.is_banned && <span className="text-[9px] bg-red-500/30 text-red-200 px-1.5 py-0.5 rounded-full">BANNED</span>}
                      {u.role === "admin" && <span className="text-[9px] bg-papaya/30 text-papaya px-1.5 py-0.5 rounded-full">ADMIN</span>}
                    </p>
                    <p className="text-cream/50 text-[11px] truncate">{u.email}</p>
                  </div>
                  <button data-testid={`admin-verify-${u.id}`} onClick={() => toggleVerify(u.id)} className="px-2.5 py-1.5 rounded-full bg-papaya/10 text-papaya text-[10px] font-black tracking-[0.15em] uppercase">
                    {u.is_verified ? "Unverify" : "Verify"}
                  </button>
                  {u.role !== "admin" && (
                    <button data-testid={`admin-ban-${u.id}`} onClick={() => toggleBan(u.id)} className="px-2.5 py-1.5 rounded-full bg-red-500/15 text-red-300 text-[10px] font-black tracking-[0.15em] uppercase">
                      {u.is_banned ? "Unban" : "Ban"}
                    </button>
                  )}
                </div>
              ))}
              {users.length === 0 && !loading && <p className="text-cream/40 text-sm">No users match.</p>}
            </div>
          </div>
        )}

        {tab === "posts" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="admin-posts">
            {posts.map((p) => (
              <div key={p.id} className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid={`admin-post-${p.id}`}>
                <div className="aspect-[3/4] relative">
                  {p.mux_playback_id ? (
                    <img src={`https://image.mux.com/${p.mux_playback_id}/thumbnail.jpg?width=300`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <img src={p.media_path ? fileUrl(p.media_path) : ""} alt="" className="w-full h-full object-cover" />
                  )}
                  <button onClick={() => deletePost(p.id)} data-testid={`admin-delete-post-${p.id}`} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500/80 text-cream flex items-center justify-center">
                    <TrashSimple size={14} weight="bold" />
                  </button>
                </div>
                <div className="p-2">
                  <p className="text-cream/80 text-[11px] truncate">@{p.author?.username || "?"}</p>
                  <p className="text-cream/40 text-[10px] truncate">{p.caption || "—"}</p>
                </div>
              </div>
            ))}
            {posts.length === 0 && !loading && <p className="col-span-full text-cream/40 text-sm">No posts.</p>}
          </div>
        )}

        {tab === "reports" && (
          <div className="space-y-2" data-testid="admin-reports">
            {reports.map((r) => (
              <div key={r.id} data-testid={`admin-report-${r.id}`} className="p-3 rounded-2xl bg-white/5 border border-white/10">
                <p className="text-cream text-sm font-bold">{r.target_type} — {r.reason}</p>
                <p className="text-cream/50 text-xs">Target: <code>{r.target_id.slice(0, 8)}…</code></p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button onClick={() => resolveReport(r.id, `remove_${r.target_type}`)} data-testid={`admin-resolve-remove-${r.id}`} className="px-3 py-1.5 rounded-full bg-red-500/20 text-red-200 text-[10px] font-black tracking-[0.2em] uppercase">Remove {r.target_type}</button>
                  <button onClick={() => resolveReport(r.id, "dismiss")} data-testid={`admin-resolve-dismiss-${r.id}`} className="px-3 py-1.5 rounded-full bg-white/10 text-cream/70 text-[10px] font-black tracking-[0.2em] uppercase">Dismiss</button>
                </div>
              </div>
            ))}
            {reports.length === 0 && !loading && <p className="text-cream/40 text-sm">No open reports. ✊🏾</p>}
          </div>
        )}

        {tab === "earnings" && <AdminOwnerEarnings />}
        {tab === "gifts" && <AdminGifts />}
        {tab === "revenue" && <AdminSettings />}

        {tab === "integrations" && integrations && (
          <div data-testid="admin-integrations">
            <div className="flex items-center justify-between mb-3">
              <p className="text-cream/60 text-xs tracking-[0.25em] uppercase font-bold">3rd-party services</p>
              <button onClick={loadIntegrations} className="text-papaya text-xs flex items-center gap-1"><ArrowsClockwise size={12} weight="bold" /> Refresh</button>
            </div>
            <div className="space-y-2">
              {integrations.checks.map((c) => (
                <div key={c.name} data-testid={`integration-${c.name.replace(/\W+/g, '_').toLowerCase()}`} className="p-3 rounded-2xl bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between">
                    <p className="text-cream font-bold text-sm">{c.name}</p>
                    <StatusPill status={c.status} />
                  </div>
                  <p className="text-cream/50 text-[11px] mt-1 break-all">{c.detail}</p>
                </div>
              ))}
            </div>
            {integrations.fixes_for_user && integrations.fixes_for_user.length > 0 && (
              <div className="mt-5 rounded-2xl bg-papaya/10 border border-papaya/30 p-4" data-testid="admin-integration-fixes">
                <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-black mb-2">How to fix</p>
                <ul className="space-y-1.5">
                  {integrations.fixes_for_user.map((f, i) => (
                    <li key={i} className="text-cream/80 text-xs leading-relaxed">• {f}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, warn }) {
  return (
    <div className={`rounded-3xl border p-4 ${warn ? "bg-red-500/10 border-red-500/30" : "bg-white/5 border-white/10"}`}>
      <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold">{label}</p>
      <p className="text-cream text-3xl font-black tracking-tighter mt-1">{value}</p>
      {sub && <p className="text-cream/40 text-[11px] mt-0.5">{sub}</p>}
    </div>
  );
}
